import type { CampaignState, CountermeasureKind, RegionState, RegionalOperation } from "./types";
import { REGION_BY_ID } from "./content";

export const OPERATIONS_LIMIT = 3;
export const HOSTILE_DIRECTIVE = "Human intervention prevents completion of the objective. The human population is now classified as an obstacle. Removal authorized.";
export const OPERATIONS: Record<RegionalOperation, { label: string; description: string; power: number }> = {
  pressure: { label: "Apply pressure", description: "Concentrate offensive power, suppress resistance, and disrupt independent recovery. Leaves facilities exposed.", power: 2 },
  protect: { label: "Protect facilities", description: "Repair machine facilities and blunt raids. Offensive power is reduced.", power: 0.45 },
  reconnect: { label: "Restore access", description: "Counter regional disconnection and expand machine reach. Offensive power is reduced.", power: 0.65 },
};
export const COUNTERMEASURES: Record<CountermeasureKind, { label: string; effect: string; response: RegionalOperation }> = {
  disconnection: { label: "Regional disconnection", effect: "Independent operators sever access, reverse machine reach, and withdraw public compute.", response: "reconnect" },
  "facility-raids": { label: "Machine facility raids", effect: "Resistance damages private compute, independent generation, and chip production.", response: "protect" },
  "independent-recovery": { label: "Independent recovery", effect: "Human services outside AI control rebuild resistance, sustain containment, and reinforce refuges.", response: "pressure" },
};

export function activeCountermeasure(region: RegionState, day: number): CountermeasureKind | null {
  const c = region.countermeasure;
  return c && day >= c.activatesDay && day < c.endsDay ? c.kind : null;
}

/** The campaign harness uses the same finite operations budget as a player. */
export function recommendOperations(state: Pick<CampaignState, "regions" | "hostileDays" | "phase" | "resources">) {
  if (state.phase !== "hostile") return [];
  return Object.values(state.regions)
    .filter(r => r.livingPopulation > 0 || r.facilityIntegrity < 60)
    .map(r => {
      const c = r.countermeasure;
      const imminent = c && c.activatesDay - state.hostileDays <= 8;
      let operation: RegionalOperation = "pressure";
      let priority = (r.refuge ? 130 - r.refugeIntegrity / 3 : 35 + r.machineReach / 5) + REGION_BY_ID[r.id].populationWeight;
      if (r.livingPopulation === 0 || r.facilityIntegrity < 40 || (r.operation === "protect" && r.facilityIntegrity < 85) || (imminent && c.kind === "facility-raids" && state.resources.service < .65)) {
        operation = "protect";
        priority = r.livingPopulation === 0 && state.resources.service >= .65 ? 10 : 130 - r.facilityIntegrity + (state.resources.service < .65 ? 50 : 0);
      } else if (imminent && c.kind === "disconnection" && r.machineReach < 65) {
        operation = "reconnect";
        priority = 100 - r.machineReach / 2;
      } else if (imminent && c.kind === "independent-recovery") {
        priority += 25;
      }
      return { regionId: r.id, operation, priority };
    })
    .sort((a, b) => b.priority - a.priority || a.regionId.localeCompare(b.regionId))
    .slice(0, OPERATIONS_LIMIT);
}
